import { CheckCheck, X } from "lucide-react";
import React, { useEffect, useState, useCallback, useRef } from "react";
import placeholder from "../../assets/placeholder.png";
import { removeStartingSlash } from "../../utills/RemoveSlash";
import moment from "moment";
import { useSelector } from "react-redux";
import XHR from "../../utills/XHR";
import { database } from "../../firebase/firebaseConfig.js";
import { ref, onChildAdded, off, onValue } from "firebase/database";

const MessageContentWithReadMore = ({ content, limit = 180 }) => {
    const [expanded, setExpanded] = useState(false);
    if (!content) return null;
    const isLong = content.length > limit;
    return (
        <div style={{ marginBottom: 4 }}>
            {isLong && !expanded ? content.slice(0, limit) + "..." : content}
            {isLong && (
                <span
                    style={{
                        color: "#f9ff00",
                        cursor: "pointer",
                        marginLeft: 4,
                        fontSize: 13,
                    }}
                    onClick={() => setExpanded((e) => !e)}
                >
                    {expanded ? "Show Less" : "Read More"}
                </span>
            )}
        </div>
    );
};

const MessageInfoSidebar = ({
    open,
    onClose,
    messageInfo,
    darkMode = false,
    conversation_id,
}) => {
    const user = useSelector((state) => state.login.userData);
    const hostname = process.env.REACT_APP_BASE_URL;

    const conversationIdRef = useRef(conversation_id);
    const newItemsRef = useRef(false);

    useEffect(() => {
        conversationIdRef.current = conversation_id;
    }, [conversation_id]);

    const [loading, setLoading] = useState(false);
    const [readBy, setReadBy] = useState([]);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!messageInfo?.id) return;
        getReadBy();
    }, [messageInfo]);

    useEffect(() => {
        // Message Receipts listener
        const receiptsRef = ref(database, "MessageReadReceipt");
        onChildAdded(receiptsRef, handleReceiptsListener);

        // handle initial items
        onValue(ref(database), handleInitialItems);

        return () => {
            off(receiptsRef, "child_added", handleReceiptsListener);
        };
    }, []);

    const handleInitialItems = () => {
        newItemsRef.current = true;
    };

    const handleReceiptsListener = useCallback((snapshot) => {
        if (!newItemsRef.current) return;
        if (snapshot.exists()) {
            let e = snapshot.val();
            if (
                parseInt(e?.conversation_id) !==
                parseInt(conversationIdRef.current)
            )
                return;
            setReadBy((prev) => {
                const combined = [...prev, e?.readers];
                // Filter unique by user_id
                const uniqueByUserId = [];
                const seen = new Set();
                for (const reader of combined) {
                    if (reader && !seen.has(reader.user_id)) {
                        seen.add(reader.user_id);
                        uniqueByUserId.push(reader);
                    }
                }
                return uniqueByUserId;
            });
        }
    }, []);

    const getReadBy = () => {
        const options = {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${user?.api_token}`,
            },
        };
        setLoading(true);
        XHR(`${hostname}/api/v1/messages/${messageInfo?.id}/read-info`, options)
            .then((res) => {
                console.log("Read by", res.data?.readers);
                setReadBy(res.data?.readers || []);
            })
            .catch((err) => {
                setError("Could not load read receipts.");
            })
            .finally(() => setLoading(false));
    };

    return (
        <div
            className={`message-info-sidebar ${open ? "open" : ""} ${
                darkMode ? "bg-black text-white" : "bg-white text-dark"
            }`}
            style={{
                position: "fixed",
                top: "var(--header-height)",
                right: 0,
                height: "calc(100vh - var(--header-height))",
                width: 360,
                boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
                transform: open ? "translateX(0)" : "translateX(100%)",
                transition: "transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                zIndex: 20,
                display: "flex",
                flexDirection: "column",
            }}
        >
            <div
                className="d-flex align-items-center justify-content-between p-3 border-bottom"
                style={{
                    position: "sticky",
                    top: 0,
                    background: darkMode ? "#000" : "#fff",
                    zIndex: 21,
                }}
            >
                <h5 className="mb-0">Message Info</h5>
                <X
                    onClick={onClose}
                    size={24}
                    color={darkMode ? "#fff" : "#000"}
                    style={{ cursor: "pointer" }}
                />
            </div>
            <div
                className="p-4"
                style={{ flex: 1, minHeight: 0, overflowY: "auto" }}
            >
                {messageInfo ? (
                    <>
                        {/* Selected message as a bubble */}
                        <div className="mb-4">
                            <div className={`d-flex justify-content-end`}>
                                <div
                                    className={
                                        "bg-primary text-white p-3 rounded"
                                    }
                                    style={{
                                        borderRadius: 14,
                                        borderBottomLeftRadius:
                                            messageInfo.sender_user_id !==
                                            messageInfo.user_id
                                                ? 0
                                                : 14,
                                        borderBottomRightRadius:
                                            messageInfo.sender_user_id ===
                                            messageInfo.user_id
                                                ? 0
                                                : 14,
                                        maxWidth: 260,
                                        wordBreak: "break-word",
                                        fontSize: 14,
                                        boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
                                    }}
                                >
                                    {/* Show image if present */}
                                    {messageInfo.attachment_file ||
                                    messageInfo.offlineImg ? (
                                        <div style={{ marginBottom: 8 }}>
                                            <img
                                                src={
                                                    messageInfo.attachment_file
                                                        ? `${hostname}/${removeStartingSlash(
                                                              messageInfo.attachment_file
                                                          )}`
                                                        : messageInfo.offlineImg
                                                }
                                                alt="attachment"
                                                style={{
                                                    maxWidth: 220,
                                                    maxHeight: 220,
                                                    borderRadius: 10,
                                                    display: "block",
                                                    marginBottom: 6,
                                                    background: "#eee",
                                                }}
                                                onError={(e) => {
                                                    e.target.onerror = null;
                                                    e.target.src = placeholder;
                                                }}
                                            />
                                        </div>
                                    ) : null}
                                    <MessageContentWithReadMore
                                        content={messageInfo.content}
                                    />
                                    <div
                                        className="text-white-50 d-flex justify-content-end"
                                        style={{ fontSize: 12 }}
                                    >
                                        {moment
                                            .utc(
                                                messageInfo?.created_at,
                                                "YYYY-MM-DD HH:mm:ss"
                                            )
                                            .local()
                                            .format("hh:mm A")}
                                    </div>
                                </div>
                            </div>
                        </div>
                        {/* Read By section */}
                        <div className="mt-4">
                            <strong>
                                Read By
                                <CheckCheck
                                    className="text-primary"
                                    size={24}
                                    style={{ marginLeft: 4 }}
                                />
                            </strong>

                            {loading ? (
                                <div className="mt-2">
                                    {[...Array(3)].map((_, idx) => (
                                        <div
                                            key={idx}
                                            className="d-flex align-items-center mb-3"
                                        >
                                            <div
                                                style={{
                                                    width: 38,
                                                    height: 38,
                                                    borderRadius: "50%",
                                                    background: darkMode
                                                        ? "#333"
                                                        : "#ececec",
                                                    marginRight: 10,
                                                    animation:
                                                        "skeleton-loading 1.2s infinite linear alternate",
                                                }}
                                            />
                                            <div
                                                style={{
                                                    width: 90,
                                                    height: 14,
                                                    borderRadius: 6,
                                                    background: darkMode
                                                        ? "#333"
                                                        : "#ececec",
                                                    animation:
                                                        "skeleton-loading 1.2s infinite linear alternate",
                                                }}
                                            />
                                        </div>
                                    ))}
                                    {/* Skeleton animation keyframes */}
                                    <style>{`
                                        @keyframes skeleton-loading {
                                            0% { opacity: 0.6; }
                                            100% { opacity: 1; }
                                        }
                                    `}</style>
                                </div>
                            ) : (
                                <div className="mt-2">
                                    {Array.isArray(readBy) &&
                                    readBy.length > 0 ? (
                                        readBy.map((user, idx) => (
                                            <div
                                                key={idx}
                                                className="d-flex align-items-center mb-3"
                                            >
                                                <img
                                                    src={
                                                        user.profile_pic
                                                            ? `${hostname}/${removeStartingSlash(
                                                                  user.profile_pic
                                                              )}`
                                                            : placeholder
                                                    }
                                                    alt={user.username}
                                                    style={{
                                                        width: 38,
                                                        height: 38,
                                                        borderRadius: "50%",
                                                        objectFit: "cover",
                                                        marginRight: 10,
                                                    }}
                                                />
                                                <span>{user.username}</span>
                                            </div>
                                        ))
                                    ) : (
                                        <div className="d-flex align-items-center justify-content-center text-muted">
                                            <span
                                                style={{
                                                    fontSize: 12,
                                                    color: darkMode
                                                        ? "#ccc"
                                                        : "#000",
                                                }}
                                            >
                                                No one has read this message
                                                yet.
                                            </span>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </>
                ) : (
                    <div className="p-4 text-center text-muted">
                        No message selected.
                    </div>
                )}
            </div>
        </div>
    );
};

export default MessageInfoSidebar;
